// CSC Load-Profile chart — renders analyzeLoadProfile()/buildSyntheticAnalysis()
// output in the HTML-simulator style the user prefers (2026-07-24): header, four
// stat cards, Daily | Hourly | Monthly toggle, and a stacked bar chart (day-import
// blue · night-import purple · solar orange · export green). The stat cards recompute
// for the selected view — a typical day / hour / month. App styling (Poppins + the
// CSC palette). Pure presentational; all numbers come from the `analysis` prop.
import React, { useState, useMemo } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PP = "'Poppins', sans-serif";
const COLORS = { text: '#0F172A', sub: '#64748B', border: '#E2E8F0', bg: '#F8FAFC' };

// Series colours — matched to the reference HTML.
const C = {
    day: '#2f9be0',      // TNB day import (8am–6pm)
    night: '#6b4ee6',    // TNB night import (6pm–8am)
    solar: '#f5a623',    // solar generated
    export: '#7cb342',   // export to grid
    battCharge: '#db2777',   // solar → battery (stored) — rose
    battDischarge: '#f9a8d4', // battery → load (discharged) — light pink
};

const fmt = (n) => (n == null ? '—' : Math.round(n).toLocaleString());
// Median over NON-ZERO entries → the typical ACTIVE value at this granularity
// (so night/off hours of zeros don't drag an hourly median down to ~0).
const medianNonZero = (arr) => {
    const a = arr.filter((x) => x > 0).sort((x, y) => x - y);
    if (!a.length) return 0;
    const m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

/* THE SPREAD, NOT JUST THE MIDDLE. A median alone hides the thing a sizing conversation
   turns on — how far the site swings. 1,395 kWh a day median reads very differently when
   the range is 1,300–1,500 than when it is 400–3,000.
   All four figures are taken over the ACTIVE entries only, the same filter the median
   already used: a night-import series is zero for every daytime hour, and counting those
   zeros would report a "lowest" of 0 on every site and an average half the truth.
   (user 2026-11-04: "median, highest, lowest average for 3 of the day time import
    export night import") */
const spreadNonZero = (arr) => {
    const a = arr.filter((x) => x > 0).sort((x, y) => x - y);
    if (!a.length) return { median: 0, high: 0, low: 0, avg: 0, n: 0 };
    const m = Math.floor(a.length / 2);
    return {
        median: a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2,
        high: a[a.length - 1],
        low: a[0],
        avg: a.reduce((s, x) => s + x, 0) / a.length,
        n: a.length,
    };
};

function StatCard({ label, value, unit, sub, color, spread, spreadScope }) {
    return (
        <Box sx={{ flex: '1 1 160px', minWidth: 150, bgcolor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '12px', p: 1.75 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub, fontFamily: PP, mb: 0.5 }}>{label}</Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color, fontFamily: PP, lineHeight: 1.1 }}>
                {value}<span style={{ fontSize: '0.85rem', fontWeight: 600, marginLeft: 4 }}>{unit}</span>
            </Typography>
            {sub && <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP, mt: 0.4 }}>{sub}</Typography>}
            {/* The median is the headline; the spread sits under it so the card answers
                "how typical is typical?" without a second chart. */}
            {spread && spread.n > 0 && (
                <Box sx={{ mt: 0.9, pt: 0.75, borderTop: `1px solid ${COLORS.border}` }}>
                    {[['Highest', spread.high], ['Average', spread.avg], ['Lowest', spread.low]].map(([k, v]) => (
                        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <Typography sx={{ fontSize: '0.64rem', color: '#94A3B8', fontFamily: PP }}>{k}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.text, fontFamily: PP }}>
                                {fmt(v)}
                            </Typography>
                        </Box>
                    ))}
                    {/* WHAT THE SPREAD IS OVER. On Hourly these three range across the 24
                        hours of a typical day, not across days — "lowest" is the quietest
                        HOUR, which is a different claim from the quietest day and was read
                        as the latter. (user 2026-11-04: "hourly u take lowest of the day?") */}
                    {spreadScope && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#CBD5E1', fontFamily: PP, mt: 0.4 }}>
                            across {spreadScope}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default function CscLoadProfileChart({ analysis, title, subtitle, peakFrac = null }) {
    const [view, setView] = useState('daily');
    const data = useMemo(() => (analysis?.charts?.[view] || []), [analysis, view]);

    // Stat cards recompute for the current view — a typical (active) day/hour/month.
    const cards = useMemo(() => ({
        // "Solar generated" card = TOTAL generation (solarGen); the orange candle itself is
        // only the self-used portion (solarGen falls back to solar for the real upload).
        solar: medianNonZero(data.map((d) => (d.solarGen != null ? d.solarGen : d.solar))),
        day: medianNonZero(data.map((d) => d.dayImport)),
        night: medianNonZero(data.map((d) => d.nightImport)),
        exp: medianNonZero(data.map((d) => d.export)),
        // Spread for the three METERED series. Solar generated is kWp × PSH every day, so
        // it has no spread to report and keeps a single figure.
        daySpread: spreadNonZero(data.map((d) => d.dayImport)),
        nightSpread: spreadNonZero(data.map((d) => d.nightImport)),
        expSpread: spreadNonZero(data.map((d) => d.export)),
        // The ORANGE candle — solar the site used itself. Separate from the "Solar
        // generated" card above, which is total output including what was exported.
        selfUsed: medianNonZero(data.map((d) => d.solar)),
    }), [data]);

    /* WHICH HALF OF THE BILL the self-used solar came off. The bars above are bucketed by
       OPERATING hours (8am–6pm), which is a different thing from the TARIFF windows: ToU
       peak runs 14:00–22:00 on weekdays only, at 31.32 sen against 27.23. So a kWh in the
       blue bar may be peak or off-peak, and the bars alone cannot say which.

       `peakFrac` comes from the engine, worked out from this site's own hours and days —
       not a constant — so a site closing at 17:00 is not credited for the 17:00–20:00
       output it exports. Absent, the row is simply not drawn rather than guessed at.
       (user 2026-10-31: "which kwh is off peak which is peak? cos both rate is diff") */
    const split = useMemo(() => {
        if (peakFrac == null || !(cards.selfUsed > 0)) return null;
        const f = Math.max(0, Math.min(1, peakFrac));
        return { peak: cards.selfUsed * f, off: cards.selfUsed * (1 - f), pct: f * 100 };
    }, [peakFrac, cards.selfUsed]);

    /* Where the import colour turns over, as a percentage of the plotted width. Points
       sit at i/(n-1), so the edge between the last night hour and the first day hour is
       halfway between their two positions. Derived from the data so a profile with a
       different window or length still breaks in the right place.
       Declared ABOVE the early return — a hook after a conditional return is called in a
       different order on the renders that bail out, which React refuses outright. */
    const windowStops = useMemo(() => {
        const n = data.length;
        const mid = (i) => (n > 1 ? ((i + 0.5) / (n - 1)) * 100 : 0);
        const firstDay = data.findIndex((d) => d.isDayHour);
        const lastDay = data.reduce((acc, d, i) => (d.isDayHour ? i : acc), -1);
        return {
            dawn: +(firstDay > 0 ? mid(firstDay - 1) : 0).toFixed(3),
            dusk: +(lastDay >= 0 && lastDay < n - 1 ? mid(lastDay) : 100).toFixed(3),
        };
    }, [data]);

    if (!analysis || !analysis.charts) return null;
    const { flags = [] } = analysis;
    const per = view === 'monthly' ? 'per month' : view === 'hourly' ? 'per hour' : 'per day';
    const grain = view === 'monthly' ? 'month' : view === 'hourly' ? 'hour' : 'day';
    // SELCo curtails the surplus (shown as kWh but NOT credited); ATAP exports it at RM0.20.
    const isSelco = analysis.mode === 'selco';
    const exportLabel = isSelco ? 'Curtailed (not counted)' : 'Export to grid';
    const exportCardLabel = isSelco ? 'Curtailed median' : 'Export median';
    // Cap the x-axis to ~8 readable ticks regardless of series length — daily can be 28
    // synthetic days (W1·Mo…) or 30–90 real-upload days (MM-DD), hourly is 24. Combined with
    // angled labels below, the shown ticks never collide into an unreadable jumble.
    // (user 2026-07-29)
    const xInterval = view === 'monthly' ? 0 : Math.max(0, Math.ceil(data.length / 8) - 1);
    // Months are discrete buckets → columns. Days and hours are a continuous shape → areas.
    const asBars = view === 'monthly';
    /* HOURLY IMPORT IS DRAWN STRAIGHT, not curved and not stepped.
       Curved (`monotone`) was the first attempt: day and night import are mutually
       exclusive, so the curve had to ease from zero to the real value across the 07:00
       and 18:00 boundaries, and the two colours bled through each other in a wedge that
       belonged to neither. Stepping fixed the boundary and turned the whole series into
       a staircase — crisp, but a wall of blocks.
       `linear` keeps the boundary change to a single straight segment and leaves the
       rest of the shape readable. Daily and monthly stay curved: both series are
       non-zero at every point there, so no boundary to cross.
       (user 2026-11-04: "day time is blue, shd be just straight diff" /
        "this graph need linear bit. now its solid") */
    const importCurve = view === 'hourly' ? 'linear' : 'monotone';
    // Daily and monthly carry both series at every point, so there they stack into the
    // grid total. Hourly never has both at once — stacking would be a no-op that only
    // confuses the null handling.
    const importStack = view === 'hourly' ? undefined : 'grid';

    // Spelt out on the cards, because "lowest" means a different thing in each view.
    const spreadScope = view === 'hourly'
        ? `${data.length} hours of a typical day`
        : `${data.length} ${view === 'monthly' ? 'month' : 'day'}${data.length === 1 ? '' : 's'}`;

    return (
        <Box sx={{ bgcolor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', p: { xs: 2, md: 2.5 }, fontFamily: PP }}>
            {/* Header */}
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                {title || 'Load Profile Analysis'}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: '0.75rem', color: COLORS.sub, fontFamily: PP, mb: 1.5 }}>{subtitle}</Typography>
            )}

            {/* Anomaly / mode flags */}
            {flags.length > 0 && (
                <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', p: 1.25, mb: 1.75 }}>
                    {flags.map((f, i) => (
                        <Typography key={i} sx={{ fontSize: '0.72rem', color: '#92400E', fontFamily: PP, lineHeight: 1.5 }}>
                            • {f.label}
                        </Typography>
                    ))}
                </Box>
            )}

            {/* Stat cards — typical value at the selected granularity */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
                <StatCard label="Solar generated" value={fmt(cards.solar)} unit="kWh" color={C.solar} sub={`typical ${grain}`} />
                <StatCard label="Day import median" value={fmt(cards.day)} unit="kWh" color={C.day}
                    sub={`8am–6pm ${per}`} spread={cards.daySpread} />
                <StatCard label="Night import median" value={fmt(cards.night)} unit="kWh" color={C.night}
                    sub={`6pm–8am ${per}`} spread={cards.nightSpread} />
                <StatCard label={exportCardLabel} value={fmt(cards.exp)} unit="kWh" color={C.export}
                    sub={`${isSelco ? 'curtailed' : 'to grid'} ${per}`} spread={cards.expSpread} />
            </Box>

            {/* Which tariff line the self-used solar actually came off. */}
            {split && (
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1.5, mb: 2,
                    bgcolor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '12px', px: 1.75, py: 1.25,
                }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub, fontFamily: PP }}>
                        Solar used on-site, against the bill
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#B45309', fontFamily: PP }}>
                        {fmt(split.peak)} kWh <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>peak @ RM0.3132</span>
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                        {fmt(split.off)} kWh <span style={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub }}>off-peak @ RM0.2723</span>
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP }}>
                        {split.pct.toFixed(1)}% peak · ToU peak is weekdays 14:00–22:00, so weekend output is always off-peak
                    </Typography>
                </Box>
            )}

            {/* View toggle */}
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => { if (v) setView(v); }}
                sx={{ mb: 1.5, '& .MuiToggleButton-root': { fontFamily: PP, textTransform: 'capitalize', fontSize: '0.74rem', fontWeight: 600, px: 1.5, py: 0.4 } }}>
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="hourly">Hourly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>

            {/* A GRAPH, NOT BARS. Thirty days × five series as grouped bars is 150 bars a
                few pixels wide: the eye can read the tallest one and nothing else. Areas
                show the same figures as a shape, so the trend across the period is legible
                at a glance. Export and solar→battery stay as LINES: neither serves the
                load, so neither belongs in a band beside one that does.
                (user 2026-11-04: "csc load profile design help me do become graph
                 instead of chart") */}
            <Box sx={{ width: '100%', height: 368 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                        {/* SOLAR IS THE SERIES THE CUSTOMER IS BUYING, so it is the only
                            one drawn at full strength: a deep amber edge over a solid
                            fill. Bought energy sits behind it, washed out — the contrast
                            is the argument, and it survives being printed in grey.
                            (user 2026-11-04: "solar graph like diff") */}
                        <defs>
                            <linearGradient id="gSolar" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={C.solar} stopOpacity={0.98} />
                                <stop offset="100%" stopColor={C.solar} stopOpacity={0.72} />
                            </linearGradient>
                            <linearGradient id="gBatt" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={C.battDischarge} stopOpacity={0.85} />
                                <stop offset="100%" stopColor={C.battDischarge} stopOpacity={0.55} />
                            </linearGradient>
                            {[['gDay', C.day], ['gNight', C.night]].map(([id, col]) => (
                                <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={col} stopOpacity={0.42} />
                                    <stop offset="100%" stopColor={col} stopOpacity={0.16} />
                                </linearGradient>
                            ))}
                            {/* HORIZONTAL gradient with DOUBLED stops — two stops at the same
                                offset give a hard edge rather than a blend, so the colour
                                turns over at the tariff boundary in zero pixels. The offsets
                                are computed from the data, not hard-coded, so they stay on
                                the boundary whatever the series length. */}
                            <linearGradient id="gWindow" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={C.night} stopOpacity={0.42} />
                                <stop offset={`${windowStops.dawn}%`} stopColor={C.night} stopOpacity={0.42} />
                                <stop offset={`${windowStops.dawn}%`} stopColor={C.day} stopOpacity={0.42} />
                                <stop offset={`${windowStops.dusk}%`} stopColor={C.day} stopOpacity={0.42} />
                                <stop offset={`${windowStops.dusk}%`} stopColor={C.night} stopOpacity={0.42} />
                                <stop offset="100%" stopColor={C.night} stopOpacity={0.42} />
                            </linearGradient>
                            <linearGradient id="gWindowLine" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={C.night} />
                                <stop offset={`${windowStops.dawn}%`} stopColor={C.night} />
                                <stop offset={`${windowStops.dawn}%`} stopColor={C.day} />
                                <stop offset={`${windowStops.dusk}%`} stopColor={C.day} />
                                <stop offset={`${windowStops.dusk}%`} stopColor={C.night} />
                                <stop offset="100%" stopColor={C.night} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F4" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: PP, fill: COLORS.sub }}
                            interval={xInterval} angle={-40} textAnchor="end" height={54} tickMargin={6} />
                        <YAxis tick={{ fontSize: 10, fontFamily: PP, fill: COLORS.sub }} width={44}
                            label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94A3B8', fontFamily: PP } }} />
                        {/* One band means one tooltip row, so the row has to say WHICH window
                            the hovered hour falls in — those are different tariffs and the
                            colour alone should not have to carry it. */}
                        <Tooltip contentStyle={{ fontFamily: PP, fontSize: '0.72rem', borderRadius: 8, border: `1px solid ${COLORS.border}` }}
                            formatter={(val, name, item) => {
                                if (name !== 'TNB import') return [`${fmt(val)} kWh`, name];
                                const day = item?.payload?.isDayHour;
                                return [`${fmt(val)} kWh`, `TNB ${day ? 'day' : 'night'} import (${day ? '8am–6pm' : '6pm–8am'})`];
                            }} />
                        <Legend wrapperStyle={{ fontFamily: PP, fontSize: '0.7rem' }} />

                        {/* What served the load, stacked bottom-up: own solar first, then
                            the battery, then what had to be bought. */}
                        {/* MONTHLY IS BARS, DAILY AND HOURLY ARE AREAS. A month is a discrete
                            bucket you compare against its neighbours, and twelve of them read
                            best as columns; a day or an hour is a position on a continuous
                            curve, where the SHAPE is the information and bars just chop it up.
                            (user 2026-11-04: "Monthly can chart. Hourly can graph. Daily can
                             graph too")

                            Curves are `monotone`, not a natural spline: the spline drew the
                            prettier line and then swung BELOW ZERO either side of the solar
                            day, and negative energy on a customer's chart is worse than a
                            slightly firmer curve. `monotone` bends through every point without
                            ever leaving the data's own range. */}
                        {/* WHAT MAY BE STACKED. Day and night import are two halves of one
                            meter reading, so stacking them gives the grid import — a real
                            total. Solar is NOT part of that total: `dayImport` is what the
                            meter recorded BEFORE any system exists, and `solar` is what a
                            system WOULD self-consume out of that same load. Stacking the two
                            double-counts — on the TPLI profile it drew 317,512 kWh against a
                            true load of 204,575. The old grouped bars kept solar separate for
                            exactly this reason; it keeps its own band here. */}
                        {asBars ? (<>
                            <Bar dataKey="dayImport" stackId="grid" name="TNB day import (8am–6pm)"
                                fill={C.day} fillOpacity={0.42} />
                            <Bar dataKey="nightImport" stackId="grid" name="TNB night import (6pm–8am)"
                                fill={C.night} fillOpacity={0.42} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="solar" name="Solar used on-site"
                                fill={C.solar} stroke="#E8890C" strokeWidth={1} radius={[4, 4, 0, 0]} />
                            {analysis.hasBatt && (
                                <Bar dataKey="battDischarge" name="Battery → load"
                                    fill={C.battDischarge} fillOpacity={0.85} radius={[4, 4, 0, 0]} />
                            )}
                        </>) : (<>
                            {/* STEPPED ON THE HOURLY VIEW. Day and night import are mutually
                                exclusive — night is zero for every daytime hour and the
                                other way round — so a smooth curve has to ramp from zero to
                                the real value across the 07:00–08:00 and 17:00–18:00
                                boundaries, and the two colours bleed through each other in
                                a wedge that belongs to neither. A step puts a vertical edge
                                exactly where the tariff window changes, which is also the
                                honest shape: an hourly figure is a bucket, flat across its
                                hour, not a point on a curve.
                                Daily and monthly keep the smooth curve — there both series
                                are non-zero every point, so there is no boundary to ramp
                                across. (user 2026-11-04: "day time is blue, shd be just
                                straight diff. now i can see its weird combined") */}
                            {/* ONE BAND, TWO COLOURS, on the hourly view. The site imports
                                around the clock — the day/night split is a tariff boundary,
                                not a break in the meter — so the import is a single
                                continuous series, coloured by a gradient whose stops sit
                                exactly on the window edges. That gives the vertical colour
                                change with no diagonal and no hole, which two separate
                                bands could not do: zeros made the line climb from the axis,
                                nulls left a blank hour.
                                Daily and monthly keep the two stacked bands — there both
                                are non-zero at every point and the stack IS the total.
                                (user 2026-11-04: "u need combine right?" / "the graph need
                                 to link") */}
                            {view === 'hourly' ? (
                                <Area type="linear" dataKey="gridImport" name="TNB import"
                                    stroke="url(#gWindowLine)" fill="url(#gWindow)" strokeWidth={1.75} />
                            ) : (<>
                                <Area type={importCurve} dataKey="dayImport" stackId={importStack} name="TNB day import (8am–6pm)"
                                    stroke={C.day} fill="url(#gDay)" strokeWidth={1.5} strokeOpacity={0.9} />
                                <Area type={importCurve} dataKey="nightImport" stackId={importStack} name="TNB night import (6pm–8am)"
                                    stroke={C.night} fill="url(#gNight)" strokeWidth={1.5} strokeOpacity={0.9} />
                            </>)}
                            <Area type="monotone" dataKey="solar" name="Solar used on-site"
                                stroke="#E8890C" fill="url(#gSolar)" strokeWidth={2.5}
                                activeDot={{ r: 4, strokeWidth: 0 }} />
                            {analysis.hasBatt && (
                                <Area type="monotone" dataKey="battDischarge" name="Battery → load"
                                    stroke={C.battDischarge} fill="url(#gBatt)" strokeWidth={1.75} />
                            )}
                        </>)}

                        {/* Energy that did not serve the load — kept off the stack. */}
                        {analysis.hasBatt && (
                            <Line type="monotone" dataKey="battCharge" name="Solar → battery"
                                stroke={C.battCharge} strokeWidth={2} dot={false} />
                        )}
                        <Line type="monotone" dataKey="export" name={exportLabel}
                            stroke={isSelco ? '#94A3B8' : C.export} strokeWidth={2} dot={false}
                            strokeDasharray={isSelco ? '4 3' : undefined} />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
}
